import {
  Box,
  Button,
  ExpandableSection,
  Modal,
  Pagination,
  Table,
} from '@amzn/awsui-components-core';
import { useCollection } from '@amzn/awsui-collection-hooks';
import {
  PublicationComment,
  PublicationCommentState,
  PublicationCommentToReview,
} from '@amzn/awscat-pattern-library-service-model';
import { DateTime } from 'luxon';
import React, { useCallback, useEffect, useState } from 'react';
import { MplServiceClientFactory } from '../../api/MplServiceClientFactory';
import constants from '../../common/constants';
import '../../resources/styles/scss/components/pages/content/PublishPendingComments.css';
import Breadcrumbs from '../common/Breadcrumbs';
import PageCanvas from '../common/PageCanvas';
import RenderMultilineText from '../common/RenderMultilineText';
import PublicationLink from '../publication/PublicationLink';
import { tablePaginationAriaLabels } from '../../api/UtilsApi';
import { DEFAULT_PREFERENCES, Preferences } from '../common/CommonComponents';

const mplServiceClient = MplServiceClientFactory.create();

const PublishPendingComments = () => {
  const [currentlySelectedComments, setCurrentlySelectedComments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isPublishingComment, setIsPublishingComment] = useState(false);
  const [pendingComments, setPendingComments] = useState<PublicationCommentToReview[]>([]);
  const [preferences, setPreferences] = useState(DEFAULT_PREFERENCES);

  const refreshComments = useCallback(async () => {
    setIsLoading(true);
    const { comments } = await mplServiceClient.getCommentsToPublish();
    setIsLoading(false);
    setPendingComments(comments);
  }, []);

  useEffect(() => {
    refreshComments();
  }, [refreshComments]);

  // @ts-expect-error TS7006 - strict migration
  const renderComment = (comment) => {
    if (comment.length < 50) {
      return comment;
    }

    const commentHeader = comment.slice(0, 50) + '...';
    return (
      <ExpandableSection
        variant="navigation"
        className="custom-comments-expandable-section"
        header={commentHeader}
      >
        <div className="commentbox">{comment}</div>
      </ExpandableSection>
    );
  };

  const renderCommentsInModal = () => {
    return currentlySelectedComments.map((c) => (
      // @ts-expect-error TS2339 - strict migration
      <div key={c.commentId} className="commentbox">
        {/* @ts-expect-error TS2339 - strict migration */}
        <PublicationLink contentId={c.contentId} contentName={c.contentId} />
        {/* @ts-expect-error TS2339 - strict migration */}
        <RenderMultilineText text={c.comment} />
      </div>
    ));
  };

  const publishComment = async () => {
    setIsPublishingComment(true);

    await Promise.all(
      currentlySelectedComments.map((c) =>
        mplServiceClient.updateCommentState(c, PublicationCommentState.PUBLISHED),
      ),
    );

    setIsModalVisible(false);
    setIsPublishingComment(false);
    await refreshComments();
  };

  const renderEmptyContent = () => {
    return (
      <div>
        {constants.NO_PENDING_COMMENTS_FOUND_FOR_PUBLISHER}
        <br />
      </div>
    );
  };

  const renderTableHeader = () => {
    const disabled = currentlySelectedComments.length === 0;
    return (
      <span className="comment-to-publish-header">
        <Box variant="h3" float="left">
          {constants.PUBLISHER_COMMENTS_HEADER}
        </Box>
        <p>
          <Button disabled={disabled} variant="primary" onClick={() => setIsModalVisible(true)}>
            {constants.PUBLISH_BUTTON_TEXT}
          </Button>
        </p>
      </span>
    );
  };

  const selectedItems = (event: any) => {
    const selectedComments: PublicationCommentToReview[] = Array.from(event.detail.selectedItems);
    // @ts-expect-error TS2345 - strict migration
    setCurrentlySelectedComments(selectedComments);
  };

  const getColumnDefinition = () => {
    return [
      {
        // @ts-expect-error TS7006 - strict migration
        cell: (item) => renderComment(item.comment),
        header: 'Comment',
        id: 'comment',
        sortingField: 'comment',
      },
      {
        // @ts-expect-error TS7006 - strict migration
        cell: (item) => <PublicationLink contentId={item.contentId} contentName={item.contentId} />,
        header: 'Content',
        id: 'contentId',
        sortingField: 'contentId',
      },
      {
        // @ts-expect-error TS7006 - strict migration
        cell: (item) => item.userName,
        header: 'Created By',
        id: 'createdBy',
        sortingField: 'userName',
      },
      {
        cell: (item: PublicationComment) => item.createdAt.toLocaleString(DateTime.DATETIME_FULL),
        header: 'Created Date',
        id: 'createdAt',
        sortingField: 'createdAt',
      },
    ];
  };

  const columnDefinition = getColumnDefinition();
  const { items, collectionProps, paginationProps } = useCollection(pendingComments, {
    sorting: {
      defaultState: {
        // @ts-expect-error TS2322 - strict migration
        sortingColumn: columnDefinition.find((cd) => cd.id === 'contentId'),
        isDescending: true,
      },
    },
    selection: {
      keepSelection: true,
    },
    pagination: { pageSize: preferences.pageSize },
  });

  const renderCommentsToPublishTable = () => {
    return (
      <Table
        {...collectionProps}
        columnDefinitions={columnDefinition}
        items={items}
        empty={renderEmptyContent()}
        header={renderTableHeader()}
        selectionType="multi"
        resizableColumns
        wrapLines={preferences.wrapLines}
        selectedItems={currentlySelectedComments}
        onSelectionChange={selectedItems}
        pagination={<Pagination {...paginationProps} ariaLabels={tablePaginationAriaLabels} />}
        preferences={
          <Preferences disabled={false} preferences={preferences} setPreferences={setPreferences} />
        }
        ariaLabels={{ tableLabel: 'Pending comments table' }}
      />
    );
  };

  const onDismiss = () => {
    setIsModalVisible(false);
  };

  const breadcrumbItems = [
    { text: constants.APP_NAME_BREAD_CRUMB, href: '/' },
    { text: constants.PUBLISHER_COMMENTS_HEADER, href: '' },
  ];

  const modalActions = (
    <Box float="right">
      <Button variant="link" disabled={isPublishingComment} onClick={onDismiss}>
        Cancel
      </Button>
      <Button variant="primary" loading={isPublishingComment} onClick={publishComment}>
        {constants.PUBLISH_BUTTON_TEXT}
      </Button>
    </Box>
  );
  return (
    <div>
      <Breadcrumbs items={breadcrumbItems} />
      <PageCanvas showLoadingIndicator={isLoading}>{renderCommentsToPublishTable()}</PageCanvas>
      <Modal
        onDismiss={onDismiss}
        visible={isModalVisible}
        header={constants.PUBLISH_A_COMMENT_MODAL_HEADER}
        footer={modalActions}
        closeAriaLabel="Close modal"
      >
        <div>
          {constants.PUBLISH_A_COMMENT_MODAL_DESCRIPTION}
          {renderCommentsInModal()}
        </div>
      </Modal>
    </div>
  );
};

export default PublishPendingComments;
